import { useMemo, useState } from 'react'
import { useBearer, Section, TableScroller, usePagedQuery, useToast } from './_admin-utils'

interface Teacher {
  full_name?: string
  email?: string
}

interface Budget {
  budget_id: string
  teacher_id: string
  fiscal_year: string
  allocated_cents: number
  spent_cents: number
  remaining_cents: number
  locked: boolean
  teacher?: Teacher
}

interface FetcherParams {
  page: number
  pageSize: number
}

function formatCurrency(cents: number | null | undefined): string {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100)
}

export default function AdminBudgets() {
  const bearer = useBearer()
  const [teacherFilter, setTeacherFilter] = useState('')
  const [fiscalYear, setFiscalYear] = useState('')
  const { show, ToastHost } = useToast()

  const fetcher = useMemo(() => {
    return async ({ page, pageSize }: FetcherParams): Promise<{ items: Budget[]; total: number }> => {
      if (!bearer) return { items: [], total: 0 }
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) })
      if (teacherFilter.trim()) params.set('teacher', teacherFilter.trim())
      if (fiscalYear.trim()) params.set('fiscal_year', fiscalYear.trim())
      const response = await fetch(`/api/v1/admin/budgets?${params.toString()}`, {
        headers: { ...bearer },
      })
      const payload = await response.json().catch(() => ({})) as { data?: { items?: Budget[]; total?: number }; items?: Budget[]; total?: number; error?: string } | null
      if (!response.ok) throw new Error(payload?.error || 'Failed to load budgets')
      return { items: payload?.data?.items ?? payload?.items ?? [], total: payload?.data?.total ?? payload?.total ?? 0 }
    }
  }, [bearer, teacherFilter, fiscalYear])

  const { items, page, pageCount, load, loading, error } = usePagedQuery(fetcher, [fetcher], 20)

  const handleEdit = async (budget: Budget) => {
    if (!bearer) return
    const current = ((budget?.allocated_cents || 0) / 100).toFixed(2)
    const input = window.prompt(`Set allocation for FY ${budget.fiscal_year}`, current)
    if (!input && input !== '') return
    const value = Number.parseFloat(input)
    if (!Number.isFinite(value) || value < 0) {
      alert('Enter a valid dollar amount')
      return
    }
    const amount_cents = Math.round(value * 100)
    const response = await fetch('/api/v1/admin/budgets/upsert', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer },
      body: JSON.stringify({
        teacher_id: budget.teacher_id,
        fiscal_year: budget.fiscal_year,
        amount_cents,
      }),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      alert(payload?.error || 'Unable to update budget')
      return
    }
    show('Budget saved')
    load(page)
  }

  const handleLockToggle = async (budget: Budget) => {
    if (!bearer) return
    const response = await fetch('/api/v1/admin/budgets/:id/lock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...bearer },
      body: JSON.stringify({ budget_id: budget.budget_id, locked: !budget.locked }),
    })
    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      alert(payload?.error || 'Unable to update lock state')
      return
    }
    show(budget.locked ? 'Budget unlocked' : 'Budget locked')
    load(page)
  }

  return (
    <Section
      title="Teacher Budgets"
      actions={
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={teacherFilter}
            onChange={(event) => setTeacherFilter(event.target.value)}
            placeholder="Filter by teacher ID"
            className="rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={fiscalYear}
            onChange={(event) => setFiscalYear(event.target.value)}
            placeholder="Fiscal year"
            className="rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded-full bg-cocoa px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow"
          >
            Refresh
          </button>
        </div>
      }
    >
      <ToastHost />
      {loading ? (
        <p className="text-sm text-cocoa/70">Loading budgets…</p>
      ) : error ? (
        <p className="text-sm text-brick">{error}</p>
      ) : items.length ? (
        <TableScroller>
          <table className="min-w-full divide-y divide-white/60 text-sm text-cocoa/80">
            <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/60">
              <tr>
                <th className="px-3 py-2 text-left">Teacher</th>
                <th className="px-3 py-2 text-left">Fiscal Year</th>
                <th className="px-3 py-2 text-right">Allocated</th>
                <th className="px-3 py-2 text-right">Spent</th>
                <th className="px-3 py-2 text-right">Remaining</th>
                <th className="px-3 py-2 text-center">Locked</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/60">
              {items.map((budget) => (
                <tr key={budget.budget_id}>
                  <td className="px-3 py-3">
                    <div className="font-medium text-cocoa">{budget.teacher?.full_name || '—'}</div>
                    <div className="text-xs text-cocoa/60">{budget.teacher?.email || budget.teacher_id}</div>
                  </td>
                  <td className="px-3 py-3">{budget.fiscal_year}</td>
                  <td className="px-3 py-3 text-right font-semibold text-cocoa">{formatCurrency(budget.allocated_cents)}</td>
                  <td className="px-3 py-3 text-right">{formatCurrency(budget.spent_cents)}</td>
                  <td
                    className={`px-3 py-3 text-right font-semibold ${
                      budget.remaining_cents < 0 ? 'text-brick' : 'text-cocoa'
                    }`}
                  >
                    {formatCurrency(budget.remaining_cents)}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        budget.locked ? 'bg-cocoa text-white' : 'bg-shell text-cocoa/70'
                      }`}
                    >
                      {budget.locked ? 'Locked' : 'Open'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(budget)}
                        className="rounded-full border border-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLockToggle(budget)}
                        className="rounded-full border border-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell"
                      >
                        {budget.locked ? 'Unlock' : 'Lock'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      ) : (
        <p className="text-sm text-cocoa/70">No budgets found.</p>
      )}
      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={() => load(Math.max(page - 1, 1))}
          disabled={page <= 1}
          className="mr-3 rounded-full border border-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-cocoa disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-xs uppercase tracking-wide text-cocoa/60">Page {page} of {pageCount}</span>
        <button
          type="button"
          onClick={() => load(Math.min(page + 1, pageCount))}
          disabled={page >= pageCount}
          className="ml-3 rounded-full border border-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-cocoa disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </Section>
  )
}
